/**
 * Where the signed-in provider's or patient's analytics identity waits for PostHog.
 *
 * `posthog.init()` runs in `PostHogProvider`, the root-most provider in the tree. React fires
 * effects child-first, so every effect below it runs before PostHog exists. An identity applied
 * from a nested effect therefore always loses the race against the first `$pageview`, and
 * `$groups` is written onto an event at capture time and is never backfilled — those events drop
 * out of every per-practice insight, permanently.
 *
 * Render runs top-down and finishes for the whole tree before any effect. So the practice shell
 * stages its server-resolved identity during render, and `PostHogProvider` reads it inside the
 * same effect that calls `init()`, before it lets the first pageview fire. A later stage — the
 * provider switches practice without a reload — reaches the listener directly.
 */

export interface AnalyticsIdentityGroup {
  /** PostHog group type, e.g. `practice`. */
  type: string;
  /** The group's key within that type. */
  key: string;
  properties: Record<string, string>;
}

export interface AnalyticsIdentity {
  /** PostHog distinct id. The provider's `Practitioner` id or the patient's `Patient` id. */
  distinctId: string;
  /**
   * Person properties. Business metadata only — never patient PHI.
   *
   * `null` is a value, not an absence. `posthog.identify` `$set`s the keys it receives and leaves
   * every other key on the person as it was, so a key that is sometimes omitted keeps a stale
   * answer. A property that does not always resolve carries `null` instead.
   */
  personProperties: Record<string, string | boolean | null>;
  /**
   * The group every subsequent event on this session carries. Absent for a patient, so patient
   * events stay out of the practice group and the per-practice insights built on it.
   */
  group?: AnalyticsIdentityGroup;
}

type AnalyticsIdentityListener = (identity: AnalyticsIdentity) => void;

let stagedIdentity: AnalyticsIdentity | undefined;
let listener: AnalyticsIdentityListener | undefined;

/** Publishes an identity. Safe to call before PostHog initializes; that is the point. */
export function stageAnalyticsIdentity(identity: AnalyticsIdentity): void {
  stagedIdentity = identity;
  listener?.(identity);
}

/**
 * Subscribes to staged identities and replays what is already staged.
 * One subscriber at a time: `PostHogProvider` owns every call into `posthog`.
 */
export function subscribeToAnalyticsIdentity(next: AnalyticsIdentityListener): () => void {
  listener = next;

  if (stagedIdentity) {
    next(stagedIdentity);
  }

  return () => {
    listener = undefined;
  };
}

/** Test seam. Drops the staged identity and the subscriber. */
export function resetAnalyticsIdentity(): void {
  stagedIdentity = undefined;
  listener = undefined;
}
